<template>
  <header
    class="app-topbar"
    :class="['app-topbar--' + mode, { 'is-raised': !!openPanel }]"
    data-test="app-shell-topbar-panel"
  >
    <!-- Phone (1h): ☰ · page title · search icon · bell -->
    <template v-if="isPhone">
      <button
        type="button"
        class="app-topbar__icon-btn"
        aria-label="Menu"
        data-test="app-topbar-menu"
        @click="$emit('menu')"
      ><AppIcon name="menu" :size="21" /></button>
      <div class="app-topbar__title" data-test="app-topbar-title">{{ pageTitle }}</div>
      <button
        type="button"
        class="app-topbar__icon-btn"
        aria-label="Search patients, tasks or pages"
        data-test="app-topbar-search-btn"
        @click="openPhoneSearch"
      ><AppIcon name="search" :size="21" /></button>
    </template>

    <!-- Desktop (1d) 440px / tablet (1g) flexible search in the bar -->
    <div v-else class="app-topbar__search">
      <AppSearch
        ref="search"
        variant="bar"
        v-bind="searchProps"
        :open="searchOpen"
        @open="onSearchOpen"
        @close="onSearchClose"
        @pick-page="$emit('pick-page', $event)"
        @pick-patient="$emit('pick-patient', $event)"
        @pick-task="$emit('pick-task', $event)"
        @refresh="$emit('refresh-patients')"
      />
    </div>

    <!-- Always visible while masking is on (same rule as the classic header): you never leave it on
         by accident after a recording, and anyone watching can see the data is not real. -->
    <span
      v-if="maskOn"
      class="app-topbar__mask"
      title="Patient data is faked. Outbound sends are blocked."
      data-test="app-topbar-mask-pill"
    >
      <AppIcon name="eyeOff" :size="13" :stroke="2.2" />
      <span>{{ isPhone ? 'DEMO' : 'DEMO MASK ON' }}</span>
    </span>

    <div class="app-topbar__right">
      <CreditBalancePill v-if="!isPhone" pill-id="credit-balance-pill-shell" class="app-topbar__credits" />

      <!-- Tips for THIS page: a lightbulb that only exists on a page with tips. Pulled, never pushed. -->
      <AppTipsMenu v-slot="{ count, open, toggle }" placement="below">
        <div class="app-topbar__bulb-wrap">
          <button
            type="button"
            class="app-topbar__icon-btn app-topbar__bulb"
            :class="{ 'is-active': open }"
            :aria-label="'Tips for this page, ' + count"
            :title="count === 1 ? '1 tip for this page' : count + ' tips for this page'"
            aria-haspopup="dialog"
            :aria-expanded="open ? 'true' : 'false'"
            data-test="app-topbar-tips"
            @click="onTipsClick(open, toggle)"
          ><AppIcon name="bulb" :size="isDesktop ? 18 : 21" /></button>
          <span class="app-topbar__bulb-count" data-test="app-topbar-tips-count" aria-hidden="true">{{ count }}</span>
        </div>
      </AppTipsMenu>

      <div class="app-topbar__bell-wrap">
        <!-- Desktop: a labelled chip (it pulses three times, then holds still). Tablet / phone have no
             room for words, so they get the larger amber dot below, with the same pulse. -->
        <button
          v-if="alertsChip"
          type="button"
          class="app-topbar__alerts-chip"
          aria-haspopup="dialog"
          data-test="app-topbar-alerts-chip"
          @click="toggle('notifications')"
        >{{ alertsChip }}</button>
        <button
          type="button"
          class="app-topbar__icon-btn app-topbar__bell"
          :class="{ 'is-active': openPanel === 'notifications' }"
          :aria-label="bellLabel"
          :title="alertsAttention ? 'Turn on desktop alerts' : null"
          aria-haspopup="dialog"
          :aria-expanded="openPanel === 'notifications' ? 'true' : 'false'"
          data-test="app-topbar-bell"
          @click="toggle('notifications')"
        ><AppIcon name="bell" :size="isDesktop ? 18 : 21" /></button>
        <span v-if="badge" class="app-topbar__badge" data-test="app-topbar-badge">{{ badge }}</span>
        <span v-if="alertsAttention && !alertsChip" class="app-topbar__alert-dot" data-test="app-topbar-alerts-dot" aria-hidden="true"></span>
      </div>

      <template v-if="!isPhone">
        <span v-if="isDesktop" class="app-topbar__divider" aria-hidden="true"></span>
        <button
          type="button"
          class="app-topbar__account"
          :class="{ 'is-active': openPanel === 'account' }"
          aria-haspopup="menu"
          :aria-expanded="openPanel === 'account' ? 'true' : 'false'"
          :aria-label="isDesktop ? null : 'Account: ' + accountName"
          data-test="app-topbar-account"
          @click="toggle('account')"
        >
          <span class="app-topbar__avatar" aria-hidden="true">
            <img v-if="avatarSrc" :src="avatarSrc" alt="" @error="avatarFailed = true" />
            <template v-else>{{ initials }}</template>
          </span>
          <span v-if="isDesktop" class="app-topbar__who" data-test="app-topbar-identity">
            <span class="app-topbar__name">{{ accountName }}</span>
            <span v-if="officeName" class="app-topbar__office">{{ officeName }}</span>
          </span>
          <span v-if="isDesktop" class="app-topbar__chev"><AppIcon name="chev" :size="15" /></span>
        </button>
      </template>
    </div>

    <AppNotificationsPopover
      :open="openPanel === 'notifications'"
      :unread-count="unreadCount"
      :alerts-prompt="alertsPrompt"
      @close="$emit('close')"
      @open-tasks="$emit('open-tasks')"
      @alerts-enable="$emit('alerts-enable')"
      @alerts-not-now="$emit('alerts-not-now')"
      @alerts-sound-replay="$emit('alerts-sound-replay')"
      @alerts-sound-dismiss="$emit('alerts-sound-dismiss')"
    />

    <AppAccountMenu
      v-if="!isPhone"
      :open="openPanel === 'account'"
      :account="account"
      :office="office"
      :can-my-account="canMyAccount"
      :can-settings="canSettings"
      :can-switch="canSwitch"
      :super-admin="superAdmin"
      :office-switch="officeSwitch"
      :mask-on="maskOn"
      :show-identity="true"
      @close="$emit('close')"
      @my-account="$emit('my-account')"
      @settings="$emit('settings')"
      @switch-site="$emit('switch-site')"
      @toggle-mask="$emit('toggle-mask')"
      @logout="$emit('logout')"
      @classic-nav="$emit('classic-nav')"
      @office-switch="$emit('office-switch', $event)"
    />

    <!-- Phone: the full-screen search sheet stays MOUNTED (hidden) so the search icon can focus its
         input inside the same tap — iOS only raises the keyboard for a synchronous focus(). -->
    <AppSearch
      v-if="isPhone"
      ref="search"
      variant="fullscreen"
      v-bind="searchProps"
      :open="searchOpen"
      @open="onSearchOpen"
      @close="onSearchClose"
      @pick-page="$emit('pick-page', $event)"
      @pick-patient="$emit('pick-patient', $event)"
      @pick-task="$emit('pick-task', $event)"
      @refresh="$emit('refresh-patients')"
    />
  </header>
</template>

<script>
/**
 * App shell — the top bar (design 1d desktop, 1g tablet, 1h phone).
 *
 * Presentational: AppShell owns which popover is open (`openPanel`) and runs every action
 * (shellActions.js). This bar only renders and emits.
 *
 *   props  mode 'desktop'|'tablet'|'phone', pageTitle (phone), maskOn, unreadCount, alertsPrompt
 *          ({ phase: 'ask'|'sound'|'', result } — the amber dot + the popover's alerts row), account
 *          { name, role, avatarUrl? }, office { name, index, count }, openPanel
 *          null|'search'|'notifications'|'account' (also 'phoneSearch' = 'search'), searchProps
 *          (v-bind to AppSearch: patients, recentPatNums, pages, dateFormat, refreshing,
 *          lastSyncedAt, kbd), canMyAccount, canSettings, canSwitch, superAdmin, officeSwitch
 *          ({ visible, target } — the office users' "classic Teamio" switch, passed through to the
 *          account menu)
 *   emits  open(name), close, menu, pick-page(to), pick-patient(patient), pick-task(row), refresh-patients,
 *          my-account, settings, switch-site, toggle-mask, logout, classic-nav, office-switch(target),
 *          open-tasks, alerts-enable, alerts-not-now, alerts-sound-replay, alerts-sound-dismiss
 *   exposes focusSearch()
 *
 * Position: fixed to the top; `left` comes from the CSS variable --shell-left (AppShell sets it to
 * the sidebar width; default 240px). While a popover is open the bar is raised to the popover layer
 * so AppShell's scrim (below $shell-z-popover) never covers the open panel.
 *
 * Prop names avoid src/mixin.js computeds (`user`, `isSuperAdmin`) — see
 * cypress/node-tests/mixin-prop-shadow-guardrail.test.js.
 */
import AppIcon from './AppIcon.vue';
import AppSearch from './AppSearch.vue';
import AppNotificationsPopover from './AppNotificationsPopover.vue';
import AppAccountMenu from './AppAccountMenu.vue';
import AppTipsMenu from './AppTipsMenu.vue';
import CreditBalancePill from '@/components/CreditBalancePill.vue';
import { badgeText } from './shellActions';
import { bellNeedsAttention, alertsChipLabel } from '@/utils/notificationPromptCopy';

export default {
  name: 'AppTopBar',
  components: { AppIcon, AppSearch, AppNotificationsPopover, AppAccountMenu, AppTipsMenu, CreditBalancePill },

  props: {
    mode: { type: String, default: 'desktop', validator: (v) => ['desktop', 'tablet', 'phone'].indexOf(v) > -1 },
    pageTitle: { type: String, default: '' },
    maskOn: { type: Boolean, default: false },
    unreadCount: { type: Number, default: 0 },
    alertsPrompt: { type: Object, default: () => ({ phase: '', result: '' }) },
    account: { type: Object, default: () => ({}) },
    office: { type: Object, default: () => ({}) },
    openPanel: { type: String, default: null },
    searchProps: { type: Object, default: () => ({}) },
    canMyAccount: { type: Boolean, default: false },
    canSettings: { type: Boolean, default: false },
    canSwitch: { type: Boolean, default: false },
    superAdmin: { type: Boolean, default: false },
    officeSwitch: { type: Object, default: () => ({ visible: false, target: null }) },
  },

  data() {
    return { avatarFailed: false };
  },

  computed: {
    isDesktop() {
      return this.mode === 'desktop';
    },
    isPhone() {
      return this.mode === 'phone';
    },
    searchOpen() {
      return this.openPanel === 'search' || this.openPanel === 'phoneSearch';
    },
    // Header.vue notificationCountDisplay: above 10 the badge reads "10+".
    badge() {
      return badgeText(this.unreadCount);
    },
    // Desktop alerts still to be turned on (or found muted). The row itself is in the popover.
    alertsAttention() {
      return bellNeedsAttention(this.alertsPrompt);
    },
    // Desktop only: the words beside the bell. '' on tablet / phone, which show the dot instead.
    alertsChip() {
      return this.isDesktop ? alertsChipLabel(this.alertsPrompt) : '';
    },
    bellLabel() {
      const base = this.badge ? `Notifications, ${this.badge} unread` : 'Notifications';
      return this.alertsAttention ? `${base}. Desktop alerts are off` : base;
    },
    accountName() {
      return (this.account && this.account.name) || '';
    },
    officeName() {
      return (this.office && this.office.name) || '';
    },
    initials() {
      const parts = this.accountName.split(/\s+/).filter(Boolean);
      if (!parts.length) return '';
      const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
      return (parts[0].charAt(0) + last).toUpperCase();
    },
    avatarSrc() {
      return !this.avatarFailed && this.account && this.account.avatarUrl ? this.account.avatarUrl : '';
    },
  },

  watch: {
    'account.avatarUrl'() {
      this.avatarFailed = false;
    },
  },

  methods: {
    /** Ctrl/Cmd+K (AppShell's window listener) and the phone search icon. Synchronous. */
    focusSearch() {
      const s = this.$refs.search;
      if (s && typeof s.focus === 'function') s.focus();
    },

    toggle(name) {
      if (this.openPanel === name) this.$emit('close');
      else this.$emit('open', name);
    },

    // Opening the tips closes whatever the shell has open (the bell, the account menu); the tips menu
    // closes itself when the bell or anything else outside it is pressed.
    onTipsClick(isOpen, toggleTips) {
      if (!isOpen && this.openPanel) this.$emit('close');
      toggleTips();
    },

    openPhoneSearch() {
      this.focusSearch();
      if (!this.searchOpen) this.$emit('open', 'search');
    },

    onSearchOpen() {
      if (!this.searchOpen) this.$emit('open', 'search');
    },

    onSearchClose() {
      if (this.searchOpen) this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './shell';

.app-topbar {
  position: fixed;
  top: 0;
  right: 0;
  left: var(--shell-left, #{$shell-sidebar-wide});
  z-index: $shell-z-chrome;
  height: $shell-bar-height;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 20px 0 24px;
  background: $fu-surface;
  border-bottom: 1px solid $fu-border;
  font-family: $fu-font-sans;
  color: $fu-text;
  transition: left $fu-dur-base $fu-ease-out;

  &.is-raised { z-index: $shell-z-popover; }
}

// ── Search ──────────────────────────────────────────────────────────────────────────────────
.app-topbar__search {
  position: relative;
  flex: 0 1 $shell-search-width;
  width: $shell-search-width;
  min-width: 0;
}

.app-topbar--tablet .app-topbar__search {
  flex: 1 1 auto;
  width: auto;
  max-width: 340px;
}

// ── Demo mask pill (amber) ──────────────────────────────────────────────────────────────────
.app-topbar__mask {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: none;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  background: $fu-warning-subtle;
  border: 1px solid #f3d38a;
  color: #7a5200;
  font-size: 11.5px;
  font-weight: $fu-fw-bold;
  letter-spacing: 0.03em;
  white-space: nowrap;
}

// ── Right group ─────────────────────────────────────────────────────────────────────────────
.app-topbar__right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
}

.app-topbar__credits {
  display: flex;
  align-items: center;
}

.app-topbar__icon-btn {
  @include fu-icon-btn(40px);
  color: $fu-neutral-600;

  &.is-active { background: $fu-neutral-100; color: $fu-text; }
}

.app-topbar__bell-wrap {
  position: relative;
  display: flex;
}

.app-topbar__badge {
  @include shell-count-badge($fu-danger);
  position: absolute;
  top: 3px;
  right: 2px;
}

// The lightbulb (tips for this page): brand green so it reads as help, not as an alert like the bell.
.app-topbar__bulb-wrap {
  position: relative;
  display: flex;
}
.app-topbar__bulb { color: $fu-brand-600; }
.app-topbar__bulb-count {
  @include shell-count-badge($fu-brand-600);
  position: absolute;
  top: 1px;
  right: -1px;
  pointer-events: none;
}

// "Turn on alerts" prompt. Both the chip and the dot pulse a ring THREE times (1.6s each, 4.8s in all,
// under the 5-second limit of WCAG 2.2.2) and then hold still; reduced-motion gets no pulse at all.
@keyframes app-topbar-alerts-pulse {
  0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.55); }
  70%, 100% { box-shadow: 0 0 0 9px rgba(245, 158, 11, 0); }
}

// Desktop: a labelled chip beside the bell.
.app-topbar__alerts-chip {
  @include fu-button-reset;
  @include fu-focus-ring;
  align-self: center;
  height: 28px;
  margin-right: 4px;
  padding: 0 11px 0 9px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 999px;
  background: $fu-warning-subtle;
  border: 1px solid #f3d38a;
  color: $fu-warning-text;
  font-size: 12px;
  font-weight: $fu-fw-bold;
  white-space: nowrap;
  animation: app-topbar-alerts-pulse 1.6s ease-out 3;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: $fu-warning;
  }

  &:hover { background: #fdeccb; }
}

// Tablet / phone: amber dot at the bell's lower right (the unread count sits at the upper right).
.app-topbar__alert-dot {
  position: absolute;
  right: 5px;
  bottom: 6px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: $fu-warning;
  border: 2px solid $fu-surface;
  pointer-events: none;
  animation: app-topbar-alerts-pulse 1.6s ease-out 3;
}

@media (prefers-reduced-motion: reduce) {
  .app-topbar__alerts-chip,
  .app-topbar__alert-dot { animation: none; }
}

.app-topbar__divider {
  width: 1px;
  height: 24px;
  margin: 0 2px;
  background: $fu-border;
}

.app-topbar__account {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 8px 0 4px;
  border-radius: 10px;
  max-width: 260px;

  &:hover,
  &.is-active { background: $fu-neutral-50; }
}

.app-topbar__avatar {
  width: 32px;
  height: 32px;
  flex: none;
  border-radius: 50%;
  overflow: hidden;
  background: $fu-brand-50;
  color: $fu-brand-700;
  font-size: 12px;
  font-weight: $fu-fw-bold;
  display: flex;
  align-items: center;
  justify-content: center;

  img { width: 100%; height: 100%; object-fit: cover; }
}

.app-topbar__who {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
  text-align: left;
}

.app-topbar__name { font-size: 13.5px; font-weight: $fu-fw-bold; @include fu-ellipsis; }
.app-topbar__office { font-size: 12px; color: $fu-neutral-500; @include fu-ellipsis; }
.app-topbar__chev { display: flex; flex: none; color: $fu-neutral-400; }

// ── Tablet (1g): 44px targets, avatar only ──────────────────────────────────────────────────
.app-topbar--tablet {
  gap: 8px;
  padding: 0 14px 0 20px;

  .app-topbar__right { gap: 4px; }
  .app-topbar__icon-btn { width: $shell-touch-target; height: $shell-touch-target; }
  .app-topbar__badge { top: 4px; right: 3px; }
  .app-topbar__account {
    width: $shell-touch-target;
    padding: 0;
    justify-content: center;
  }
}

// ── Phone (1h): ☰ · title · search · bell ───────────────────────────────────────────────────
.app-topbar--phone {
  gap: 2px;
  padding: 0 6px;

  .app-topbar__icon-btn {
    width: $shell-touch-target;
    height: $shell-touch-target;
    color: $fu-neutral-700;
  }

  .app-topbar__right { margin-left: 0; gap: 0; }
  .app-topbar__badge { top: 4px; right: 3px; }
  .app-topbar__mask { height: 24px; padding: 0 8px; font-size: 10.5px; }
}

.app-topbar__title {
  flex: 1;
  min-width: 0;
  padding-left: 4px;
  font-size: 17px;
  font-weight: $fu-fw-bold;
  @include fu-ellipsis;
}

@media print {
  .app-topbar { display: none; }
}
</style>
